import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Row, 
  Col, 
  message, 
  Typography,
  Space,
  Result,
  QRCode,
  Alert,
  Radio,
  Modal
} from 'antd';
import { 
  SafetyOutlined,
  QrcodeOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';

import LedgerProgress from '../../AccountCenter/LedgerProgress';

const { Text } = Typography;

interface StepContentProps {
  current: number;
  onNext: () => void;
  onPrev: () => void;
}

// 步骤2：登记簿开立
const LedgerRegistrationStep: React.FC<StepContentProps & { onReopen: () => void }> = ({ onNext, onReopen }) => {
  const [qrCodeVisible, setQrCodeVisible] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [demoStatus, setDemoStatus] = useState<'progress' | 'success' | 'failure'>('progress');
  const [authType, setAuthType] = useState<'contact' | 'legal_and_contact'>('contact');

  const handleGenerateQR = () => {
    setQrCodeVisible(true);
  };

  const handleConfirmAuth = async () => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (demoStatus === 'success') {
        message.success('登记簿开立成功！');
        setTimeout(onNext, 1000);
      } else if (demoStatus === 'failure') {
        Modal.error({
          title: '授权未完成',
          content: (
            <div style={{ lineHeight: 1.6 }}>
              <div style={{ marginBottom: 8 }}>
                法定代表人或联系人未完成授权，请对应人员使用微信扫描二维码完成授权。
              </div>
              <div style={{ color: '#595959' }}>
                完成授权后再点击“我已完成授权”继续。
              </div>
            </div>
          ),
          okText: '我知道了'
        });
      } else {
        setShowProgress(true);
        message.success('授权验证完成！正在查询登记簿开立进度...');
      }
    } catch (error) {
      message.error('授权验证失败，请重试');
    }
  };

  const handleReauthorize = () => {
    setShowProgress(false);
    setShowFailure(false);
    setQrCodeVisible(false);
    message.info('请重新进行授权验证');
  };

  const handleRegenerateQR = () => {
    message.success(`二维码已更新！`);
  };

  if (showProgress) {
    return (
      <LedgerProgress 
        onSuccess={onNext}
        onReauthorize={handleReauthorize}
      />
    );
  }

  if (showFailure) {
    return (
      <Card className="enterprise-step-card enterprise-fade-in">
        <div
          className="enterprise-step-card-header"
          style={{ background: '#1890ff', color: '#ffffff', padding: '16px 24px', borderRadius: '8px 8px 0 0' }}
        >
          <SafetyOutlined className="enterprise-step-icon" style={{ fontSize: '20px', marginRight: '8px' }} />
          <span style={{ fontSize: '18px', fontWeight: 600 }}>登记簿开立失败</span>
        </div>
        
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Result
            status="error"
            title="登记簿开立失败"
            subTitle="企业信息审核不通过，请检查企业信息后重新申请"
            extra={[
              <Button type="primary" key="reopen" onClick={onReopen} className="enterprise-primary-button">
                重新开立账户
              </Button>,
              <Button key="contact" onClick={() => message.info('请联系客服：400-123-4567')} className="enterprise-secondary-button">
                联系客服
              </Button>
            ]}
          />
          <div style={{ marginTop: 24, textAlign: 'left', maxWidth: 600, margin: '24px auto 0' }}>
            <Alert
              message="常见失败原因"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>营业执照信息与银行系统记录不一致</li>
                  <li>法定代表人身份信息验证失败</li>
                  <li>企业经营状态异常或已注销</li>
                  <li>企业存在不良信用记录</li>
                  <li>提交的材料不清晰或信息不完整</li>
                </ul>
              }
              type="warning"
              showIcon
              className="enterprise-alert"
            />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="enterprise-step-card enterprise-fade-in" style={{ width: '800px', margin: '24px auto 0 auto' }}>
      <div
        className="enterprise-step-card-header"
        style={{ background: '#1890ff', color: '#ffffff', padding: '16px 24px', borderRadius: '8px 8px 0 0' }}
      >
        <SafetyOutlined className="enterprise-step-icon" style={{ fontSize: '20px', marginRight: '8px' }} />
        <span style={{ fontSize: '18px', fontWeight: 600 }}>第二步：登记簿开立</span>
      </div>
      
      {!qrCodeVisible ? (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <QrcodeOutlined className="enterprise-qr-icon" style={{ fontSize: '96px', color: '#1890ff' }} />
            <div className="enterprise-qr-title">准备开立登记簿</div>
            <div className="enterprise-qr-description">
              会员信息已提交成功，现在需要通过微信扫码跳转交通银行小程序进行身份验证以开立登记簿。
            </div>
            <Alert
              message="重要提示"
              description="登记簿开立需要银行系统审核，授权完成后请耐心等待审核结果。"
              type="info"
              showIcon
              className="enterprise-alert"
              style={{ textAlign: 'left', maxWidth: 600, margin: '0 auto' }}
            />
            <Button 
              type="primary" 
              onClick={handleGenerateQR}
              icon={<QrcodeOutlined />}
              style={{
                height: '48px',
                fontSize: '16px',
                fontWeight: 600,
                padding: '0 40px',
                background: '#1890ff',
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)'
              }}
            >
              生成授权二维码
            </Button>
          </Space>
        </div>
      ) : (
        <div style={{ padding: '24px', position: 'relative' }}>

          {/* Floating Selectors */}
          <div style={{ position: 'absolute', top: 24, left: 24, zIndex: 10, background: '#fff', padding: '10px', border: '1px solid var(--enterprise-border)', borderRadius: '8px' }}>
            <div style={{marginBottom: 8, fontWeight: 500}}>授权方式</div>
            <Radio.Group 
              value={authType} 
              onChange={(e: any) => setAuthType(e.target.value)}
              optionType="button" 
              buttonStyle="solid"
            >
              <Radio.Button value='contact'>联系人授权</Radio.Button>
              <Radio.Button value='legal_and_contact'>法定代表人及联系人</Radio.Button>
            </Radio.Group>
          </div>

          <div style={{ position: 'absolute', top: 24, right: 24, zIndex: 10, background: '#fff', padding: '10px', border: '1px solid var(--enterprise-border)', borderRadius: '8px' }}>
            <div style={{marginBottom: 8, fontWeight: 500}}>演示状态选择</div>
            <Radio.Group
              value={demoStatus}
              onChange={(e: any) => setDemoStatus(e.target.value)}
              optionType="button"
            >
              <Radio.Button value="progress">📊 进度查询</Radio.Button>
              <Radio.Button value="success">✅ 直接成功</Radio.Button>
              <Radio.Button value="failure">❌ 开户失败</Radio.Button>
            </Radio.Group>
          </div>

          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div
              className="enterprise-qr-title"
              style={{ textAlign: 'center', paddingTop: '80px', fontSize: '22px', fontWeight: 700, color: '#262626', marginBottom: 8 }}
            >
              扫码授权验证
            </div>
            
            <Row justify="center" gutter={32} align="middle">
              {authType === 'legal_and_contact' && (
                <Col>
                  <Space direction="vertical" align="center" size="middle">
                    <Text strong>法定代表人授权</Text>
                    <div className="enterprise-qr-code">
                      <QRCode value="https://bank.comm.cn/auth/ledger/LEGAL_PERSON_123" size={180} />
                    </div>
                  </Space>
                </Col>
              )}
              <Col>
                <Space direction="vertical" align="center" size="middle">
                  <Text strong>联系人授权</Text>
                  <div className="enterprise-qr-code">
                    <QRCode value="https://bank.comm.cn/auth/ledger/CONTACT_PERSON_456" size={180} />
                  </div>
                </Space>
              </Col>
            </Row>

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Button 
                onClick={handleRegenerateQR}
                size="middle"
                icon={<QrcodeOutlined />}
              >
                重置二维码
              </Button>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-block',
                  textAlign: 'left',
                  maxWidth: 600,
                  width: '100%',
                  background: '#e6f4ff',
                  border: '1px solid #91caff',
                  borderRadius: 8,
                  padding: 16
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      width: 24,
                      height: 24,
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#1677ff',
                      color: '#fff',
                      borderRadius: 6,
                      fontSize: 14,
                      fontWeight: 700
                    }}
                  >
                    i
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 600, marginLeft: 8, color: '#1f1f1f' }}>
                    {authType === 'contact'
                      ? '请使用微信扫描二维码进行授权'
                      : '请法定代表人和联系人分别扫描对应二维码完成授权'}
                  </span>
                </div>

                <div style={{ display: 'grid', gap: 8, fontSize: 14, color: '#595959', lineHeight: 1.6 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: '#1677ff' }}>•</span>
                    <span>请使用微信扫描上方二维码完成授权。</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: '#1677ff' }}>•</span>
                    <span>
                      二维码有效期
                      <strong style={{ color: '#0958d9', fontWeight: 600 }}> 24 小时</strong>，超时请点击
                      <strong style={{ color: '#0958d9', fontWeight: 600 }}> 重置二维码</strong>。
                    </span>
                  </div>
                  {authType === 'legal_and_contact' && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span style={{ color: '#1677ff' }}>•</span>
                      <span>法定代表人与联系人均需完成授权后方可继续。</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="enterprise-action-buttons" style={{ marginTop: '24px', textAlign: 'center' }}>
              <Button 
                type="primary" 
                onClick={handleConfirmAuth}
                icon={<CheckCircleOutlined />}
                style={{
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: 600,
                  padding: '0 40px',
                  background: '#1890ff',
                  border: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)'
                }}
              >
                我已完成授权
              </Button>
            </div>
          </Space>
        </div>
      )}
    </Card>
  );
};

export default LedgerRegistrationStep;